import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Bell, 
  Settings, 
  LogOut, 
  Plus, 
  BarChart3, 
  MessageCircle, 
  Calendar,
  Menu,
  X,
  FileText,
  User as UserIcon,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '../hooks/use-mobile';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import type { User } from '@supabase/supabase-js';
import { useRealTimeNotifications } from '../hooks/useRealTimeNotifications';
interface LandlordHeaderProps {
  user: User | null;
  onSignOut: () => void;
}

const LandlordHeader = ({ user, onSignOut }: LandlordHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const { notifications, stats, markAsRead, clearAll } = useRealTimeNotifications();

  const getNavClasses = (path: string) => {
    const isActive = location.pathname === path;
    return isActive 
      ? "text-primary px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 bg-primary/10" 
      : "text-slate-300 hover:text-white px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-slate-800/50";
  };

  const getMobileNavClasses = (path: string) => {
    const isActive = location.pathname === path;
    return isActive 
      ? "w-full text-left text-primary px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 bg-primary/10" 
      : "w-full text-left text-slate-300 hover:text-white px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-slate-800/50";
  };

  const handleSignOut = () => {
    onSignOut();
    setMobileMenuOpen(false);
  };

  const NotificationItem = ({ notification }: { notification: any }) => {
    const Icon = (() => {
      switch (notification.type) {
        case 'application':
          return FileText;
        case 'tour':
          return Calendar;
        case 'message':
          return MessageCircle;
        case 'match':
          return CheckCircle;
        default:
          return AlertCircle;
      }
    })();
    const unread = !notification.read;
    const timeLabel = notification.created_at ? new Date(notification.created_at).toLocaleString() : '';
    return (
      <div
        className={`p-3 border-b border-slate-700/30 hover:bg-slate-800/30 transition-colors cursor-pointer ${unread ? 'bg-slate-800/20' : ''}`}
        onClick={() => {
          if (unread) markAsRead(notification.id);
          if (notification.action?.url) navigate(notification.action.url);
          setNotificationsOpen(false);
        }}
      >
        <div className="flex items-start space-x-3">
          <div className={`p-2 rounded-lg ${unread ? 'bg-primary/20 text-primary' : 'bg-slate-700/50 text-slate-400'}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${unread ? 'text-white' : 'text-slate-300'}`}>
              {notification.title}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              {notification.message}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {timeLabel}
            </p>
          </div>
          {unread && (
            <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />
          )}
        </div>
      </div>
    );
  };

  return (
    <header className="bg-slate-900/98 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4 md:space-x-8">
            <motion.div 
              className="flex items-center space-x-3 cursor-pointer group"
              onClick={() => navigate('/landlord-dashboard')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <img 
                src="/lovable-uploads/d981c4c2-888d-4322-9d83-e527475f379f.png" 
                alt="Flamingo Logo" 
                className="h-8 w-auto object-contain"
              />
            </motion.div>

            <nav className="hidden md:flex space-x-2">
              <motion.button 
                onClick={() => navigate('/landlord-dashboard')}
                className={getNavClasses('/landlord-dashboard')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Dashboard
              </motion.button>
              <motion.button 
                onClick={() => navigate('/add-listing')}
                className={`${getNavClasses('/add-listing')} flex items-center space-x-2`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Plus className="w-4 h-4" />
                <span>Add Listing</span>
              </motion.button>
              <motion.button 
                onClick={() => navigate('/landlord-analytics')}
                className={`${getNavClasses('/landlord-analytics')} flex items-center space-x-2`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Analytics</span>
              </motion.button>
              <motion.button 
                onClick={() => navigate('/landlord-messages')}
                className={`${getNavClasses('/landlord-messages')} flex items-center space-x-2`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <MessageCircle className="w-4 h-4" />
                <span>Messages</span>
              </motion.button>
              <motion.button 
                onClick={() => navigate('/landlord-tours')}
                className={`${getNavClasses('/landlord-tours')} flex items-center space-x-2`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Calendar className="w-4 h-4" />
                <span>Tours</span>
              </motion.button>
              <motion.button 
                onClick={() => navigate('/landlord-forms')}
                className={`${getNavClasses('/landlord-forms')} flex items-center space-x-2`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FileText className="w-4 h-4" />
                <span>Forms</span>
              </motion.button>
            </nav>
          </div>

          <div className="flex items-center space-x-3">
            {/* Notifications */}
            <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
              <PopoverTrigger asChild>
                <motion.button 
                  className="relative p-2 text-slate-400 hover:text-white transition-colors hover:bg-slate-800/50 rounded-xl"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <Bell className="w-5 h-5" />
                  {stats.unread > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                    >
                      {stats.unread}
                    </Badge>
                  )}
                </motion.button>
              </PopoverTrigger>
              <PopoverContent 
                align="end" 
                className="w-80 p-0 bg-slate-800/95 backdrop-blur-xl border-slate-700/50"
                sideOffset={8}
              >
                <div className="p-4 border-b border-slate-700/30">
                  <h3 className="font-semibold text-white">Notifications</h3>
                  {stats.unread > 0 && (
                    <p className="text-sm text-slate-400">{stats.unread} unread</p>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <NotificationItem key={notification.id} notification={notification} />
                    ))
                  ) : (
                    <div className="p-4 text-center text-slate-400">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No notifications yet</p>
                    </div>
                  )}
                </div>
                <div className="p-3 border-t border-slate-700/30 grid grid-cols-2 gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full text-slate-300 hover:text-white"
                    onClick={() => {
                      clearAll();
                      setNotificationsOpen(false);
                    }}
                    aria-label="Clear all notifications"
                    title="Clear all notifications"
                  >
                    Clear all
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full text-slate-300 hover:text-white"
                    onClick={() => setNotificationsOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Settings */}
            <motion.button 
              onClick={() => navigate('/settings')}
              className="p-2 text-slate-400 hover:text-white transition-colors hover:bg-slate-800/50 rounded-xl"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Settings className="w-5 h-5" />
            </motion.button>
            
            {/* User Profile */}
            <div className="flex items-center space-x-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-white">{user?.user_metadata?.full_name || 'Landlord'}</p>
                <p className="text-xs text-slate-400">{user?.email}</p>
              </div>
              
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-sm font-bold text-white">
                  {user?.user_metadata?.full_name?.charAt(0) || 'L'}
                </span>
              </div>
              
              {/* Sign Out */}
              <motion.button 
                onClick={handleSignOut}
                className="p-2 text-slate-400 hover:text-red-400 transition-colors hover:bg-slate-800/50 rounded-xl hidden md:block"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Sign Out"
              >
                <LogOut className="w-5 h-5" />
              </motion.button>

              {/* Mobile Menu Button */}
              <motion.button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-slate-400 hover:text-white transition-colors hover:bg-slate-800/50 rounded-xl"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden border-t border-slate-700/50 py-4"
            >
              <div className="space-y-2">
                <button 
                  onClick={() => {
                    navigate('/landlord-dashboard');
                    setMobileMenuOpen(false);
                  }}
                  className={getMobileNavClasses('/landlord-dashboard')}
                >
                  Dashboard
                </button>
                <button 
                  onClick={() => {
                    navigate('/add-listing');
                    setMobileMenuOpen(false);
                  }}
                  className={`${getMobileNavClasses('/add-listing')} flex items-center space-x-2`}
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Listing</span>
                </button>
                <button 
                  onClick={() => {
                    navigate('/landlord-analytics');
                    setMobileMenuOpen(false);
                  }}
                  className={`${getMobileNavClasses('/landlord-analytics')} flex items-center space-x-2`}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>Analytics</span>
                </button>
                <button 
                  onClick={() => {
                    navigate('/landlord-messages');
                    setMobileMenuOpen(false);
                  }}
                  className={`${getMobileNavClasses('/landlord-messages')} flex items-center space-x-2`}
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Messages</span>
                </button>
                <button 
                  onClick={() => {
                    navigate('/landlord-tours');
                    setMobileMenuOpen(false);
                  }}
                  className={`${getMobileNavClasses('/landlord-tours')} flex items-center space-x-2`}
                >
                  <Calendar className="w-4 h-4" />
                  <span>Tours</span>
                </button>
                <button 
                  onClick={() => {
                    navigate('/landlord-forms');
                    setMobileMenuOpen(false);
                  }}
                  className={`${getMobileNavClasses('/landlord-forms')} flex items-center space-x-2`}
                >
                  <FileText className="w-4 h-4" />
                  <span>Forms</span>
                </button>
                <button 
                  onClick={() => {
                    handleSignOut();
                  }}
                  className="w-full text-left text-red-400 hover:text-red-300 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 hover:bg-red-900/20 flex items-center space-x-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default LandlordHeader;
